/**
 * Primitivos do documento de voucher — promovidos do protótipo validado
 * (`design/src/componentes/voucher/primitivos.tsx`, Figma "08 · voucher final").
 *
 * **O voucher é UM documento**: coluna única, seções separadas por `Separator`, sem
 * abas e sem card dentro de card — documento com aba não tem versão linear para
 * PDF/e-mail. Campo `null` faz a LINHA sumir (a regra de caractere da peça).
 */
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@voll-intelligence/ui-kit/ui/collapsible';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@voll-intelligence/ui-kit/ui/tooltip';
import { cn } from '@voll-intelligence/ui-kit/utils';
import {
  ArrowsLeftRightIcon,
  CaretRightIcon,
  CheckIcon,
  CopyIcon,
  ProhibitIcon,
  ShareNetworkIcon,
  XIcon,
} from '@phosphor-icons/react';
import { PolicyBadge } from '../../policy/policy_badge.js';
import { PersonAvatar } from '../../shared/person/person_row.js';
import { useVoucherCopy, useVoucherCopyFeedback } from '../voucher_env.js';
import type {
  VoucherDocumentBlock,
  VoucherDocumentField,
  VoucherDocumentRule,
  VoucherDocumentTone,
} from '../models.js';

const TONE_CLASS: Record<VoucherDocumentTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  warning: 'bg-warning-soft text-warning-strong',
  success: 'bg-success-soft text-success-strong',
  danger: 'bg-danger-soft text-danger-strong',
};

export function VoucherStatusPill({
  label,
  tone,
}: Readonly<{ label: string; tone: VoucherDocumentTone }>) {
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-caption font-medium', TONE_CLASS[tone])}>
      {label}
    </span>
  );
}

/** Rótulo de bloco em versalete — "MAIS INFORMAÇÕES", o eyebrow do bilhete. */
export function VoucherEyebrow({ children }: Readonly<{ children: ReactNode }>) {
  return <p className="text-caption tracking-widest text-muted-foreground uppercase">{children}</p>;
}

/** Um alvo clicável com a explicação em tooltip — o que a ação FAZ, não como ela se
 *  chama: repetir o rótulo do botão numa tooltip é ruído, e num botão de ícone o
 *  rótulo nem existe. */
function WithTooltip({ text, children }: Readonly<{ text: string; children: ReactElement }>) {
  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}

/** Degrau de toque de 44pt (Apple HIG) SÓ no telefone (#2662): `icon-sm` são 28px,
 *  e no regime full-bleed da gaveta o X é a ÚNICA saída da tela (ela cobre o véu) —
 *  um alvo de 28px na borda de cima, encostado na faixa da status bar, é o que
 *  transformou a tela em beco sem saída. O glifo continua nos 16px e o `ghost` não
 *  pinta fundo: cresce a área de TOQUE, não o desenho. `max-sm:` é o degrau de
 *  640px do DS — o mesmo regime que a casca da gaveta aproxima com
 *  `max-width: 639px`; acima dele o cluster segue como o designer desenhou (lá o
 *  ponteiro é mouse e não há status bar por cima da gaveta). */
const TOP_ACTION_TOUCH = 'max-sm:size-11';

/** As ações do topo do documento — compartilhar (copia o resumo) e fechar, no canto
 *  superior direito: o mesmo botão do `SheetContent` (ghost, `icon-sm`), na mesma
 *  quina. Compartilhar é ação secundária de documento
 *  e mora no TOPO, como todo share de mercado — na saia competia com a conversa
 *  (decisão do designer, 2026-08-05). O X é sempre o ÚLTIMO, colado na quina.
 *
 *  **Pregadas no header** (spec do drawer, 2026-08-06: header fixo): o cluster é
 *  a PRIMEIRA linha da zona de identidade, FORA da área rolável — categoria, selo e
 *  o X seguem à vista enquanto o miolo rola por baixo. Linha própria, não
 *  flutuante (#2600): nos 360px do telefone eyebrow, selo e dois ícones não cabem
 *  juntos, e o cluster absoluto ainda caía sob a status bar do aparelho.
 *
 *  `data-voucher-doc-close` no X: a casca que abre o documento (o drawer de trips)
 *  precisa ACHAR o botão para dar a ele o foco de abertura — o botão mora aqui
 *  dentro e não tem ref lá fora. Atributo, não id: o documento pode nascer duas
 *  vezes na mesma página (card no chat + drawer aberto).
 *
 *  Cada handler é opt-in: sem `onShare` não há compartilhar; sem `onClose` não há X
 *  (dentro de um Sheet/Dialog que já fecha sairiam dois na mesma quina). */
export function VoucherTopActions({
  onShare,
  onClose,
}: Readonly<{ onShare?: () => void; onClose?: () => void }>) {
  const copy = useVoucherCopy();
  if (!onShare && !onClose) return null;
  return (
    <div className="flex items-center justify-end gap-1 px-3 pt-3">
      {onShare ? (
        <WithTooltip text={copy.shareTooltip}>
          <Button variant="ghost" size="icon-sm" className={TOP_ACTION_TOUCH} onClick={onShare}>
            <ShareNetworkIcon size={16} aria-hidden />
            <span className="sr-only">{copy.share}</span>
          </Button>
        </WithTooltip>
      ) : null}
      {onClose ? (
        <WithTooltip text={copy.close}>
          <Button
            variant="ghost"
            size="icon-sm"
            className={TOP_ACTION_TOUCH}
            data-voucher-doc-close
            onClick={onClose}
          >
            <XIcon size={16} aria-hidden />
            <span className="sr-only">{copy.closeSr}</span>
          </Button>
        </WithTooltip>
      ) : null}
    </div>
  );
}

/** A copyable value — the target is the WHOLE value plus the icon, not the 12px glyph alone:
 *  aiming at a target that size to copy a code is a game, not an interface. `text-start`
 *  because `<button>` centers text by default and the value aligns with the label above it.
 *
 *  The copy goes through the host's `writeClipboard` (in the embedded chat only the legacy
 *  `execCommand` path copies, #2976). And the result is SAID: without visible feedback, copying
 *  and copying nothing were the same screen. Success swaps the glyph for the check without
 *  moving the layout; failure gets a discreet line under the value, which stays visible and
 *  selectable for a manual copy. The live region ALWAYS exists, empty outside the cycle: a
 *  screen reader only narrates changes INSIDE a live region already in the tree. */
export function VoucherCopyValue({
  value,
  what,
}: Readonly<{ value: string; what: string | null | undefined }>) {
  const copy = useVoucherCopy();
  const { status, copy: write } = useVoucherCopyFeedback();
  let feedback: string | null = null;
  if (status === 'copied') feedback = copy.copied;
  else if (status === 'failed') feedback = copy.copyFailed;
  return (
    <>
      <WithTooltip text={copy.copyWhat(what ?? copy.copyFallback)}>
        <button
          type="button"
          aria-label={copy.copyValue(value)}
          className="group/copiar flex items-center gap-1.5 text-start transition-colors hover:text-muted-foreground"
          onClick={() => {
            write(value);
          }}
        >
          <span className="font-medium">{value}</span>
          {status === 'copied' ? (
            <CheckIcon
              size={12}
              weight="bold"
              className="shrink-0 text-success-strong"
              aria-hidden
            />
          ) : (
            <CopyIcon
              size={12}
              className="shrink-0 text-muted-foreground transition-colors group-hover/copiar:text-foreground"
              aria-hidden
            />
          )}
        </button>
      </WithTooltip>
      <span
        role="status"
        aria-live="polite"
        className={status === 'failed' ? 'mt-0.5 block text-detail text-danger-strong' : 'sr-only'}
      >
        {feedback}
      </span>
    </>
  );
}

/** Rótulo pequeno + valor — a unidade dos pares de campo. */
function VoucherField({ field }: Readonly<{ field: VoucherDocumentField }>) {
  return (
    <div className="grid content-start gap-0.5">
      <p className="text-caption text-muted-foreground">{field.label}</p>
      <div className="text-ui text-foreground">
        {field.copy ? (
          <VoucherCopyValue value={field.value} what={field.copy} />
        ) : (
          <span className="grid gap-0.5">
            <span className="font-medium whitespace-pre-line wrap-anywhere">{field.value}</span>
            {field.hint ? (
              <span className="text-detail font-normal text-muted-foreground">{field.hint}</span>
            ) : null}
          </span>
        )}
      </div>
    </div>
  );
}

/** Os campos em pares — 2 colunas é o teto: o documento tem ~21rem úteis. Um campo
 *  `full` quebra o par e ocupa a linha inteira. */
export function VoucherFields({ fields }: Readonly<{ fields: VoucherDocumentField[] }>) {
  const rows: VoucherDocumentField[][] = [];
  for (const field of fields) {
    const last = rows.at(-1);
    if (!field.full && last?.length === 1 && !last[0]?.full) last.push(field);
    else rows.push([field]);
  }
  return (
    <div className="grid gap-4">
      {rows.map((row) => (
        <div key={row[0]?.key} className="grid grid-cols-2 items-start gap-4">
          {row.map((field) => (
            <VoucherField key={field.key} field={field} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Um bloco de campos com o divisor que o separa do que vem antes. */
export function VoucherBlocks({ blocks }: Readonly<{ blocks: VoucherDocumentBlock[] }>) {
  return (
    <>
      {blocks.map((block) => (
        <Fragment key={block.key}>
          <Separator />
          <VoucherFields fields={block.fields} />
        </Fragment>
      ))}
    </>
  );
}

/** Linhas rótulo → valor das regras (tarifa, estadia) — valor firme à direita,
 *  `destructive` pinta o que é vetado. */
export function VoucherRules({
  title,
  rows,
  notes,
}: Readonly<{ title: string; rows: VoucherDocumentRule[]; notes?: string[] }>) {
  return (
    <>
      <Separator />
      <section className="grid gap-4">
        <h3 className="text-title text-foreground">{title}</h3>
        <div className="grid gap-2.5">
          {rows.map((rule) => (
            <div key={rule.label} className="flex items-center justify-between gap-3">
              <span className="text-ui font-normal text-foreground">{rule.label}</span>
              <span
                className={cn(
                  'text-ui text-right',
                  rule.destructive ? 'text-destructive' : 'text-foreground',
                )}
              >
                {rule.value}
              </span>
            </div>
          ))}
        </div>
        {notes && notes.length > 0 ? (
          <div className="grid gap-2">
            {notes.map((note) => (
              <p key={note} className="text-detail text-muted-foreground">
                {note}
              </p>
            ))}
          </div>
        ) : null}
      </section>
    </>
  );
}

/** O bloco de valores — só o TOTAL e o selo de política. Sem parcelas e sem segunda
 *  moeda: `VoucherView` carrega um `amount` único, e a decomposição tarifa + taxas
 *  não é persistida em lugar nenhum. Linha de composição inventada num documento de
 *  conferência é pior que ausência — a pessoa soma e não fecha. */
export function VoucherTotal({
  value,
  struck,
  policy,
}: Readonly<{
  value: string;
  struck?: boolean;
  policy?: { label: string; within: boolean } | null;
}>) {
  const copy = useVoucherCopy();
  return (
    <>
      <Separator />
      <div className="flex items-start justify-between gap-3">
        <span className="text-title text-foreground">{copy.total}</span>
        <div className="grid justify-items-end gap-1.5">
          <span
            className={cn(
              'text-title tabular-nums',
              struck ? 'text-muted-foreground line-through' : 'text-foreground',
            )}
          >
            {value}
          </span>
          {/* O selo do total é o MESMO dos recibos (`PolicyBadge`). O furo vinha
              sem escudo — num documento de conferência a cor não é canal
              suficiente. */}
          {policy ? (
            <PolicyBadge tone={policy.within ? 'within' : 'outside'} label={policy.label} />
          ) : null}
        </div>
      </div>
    </>
  );
}

/** Uma linha de "MAIS INFORMAÇÕES" — título + caret, SEM subtítulo, colapsada por
 *  padrão. Disclosure não leva tooltip: abrir e fechar é autoexplicativo. */
function VoucherDisclosure({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="group/disclosure flex w-full items-center gap-3 py-4 text-left">
        <span className="min-w-0 flex-1 text-title text-foreground">{title}</span>
        <CaretRightIcon
          size={16}
          className="shrink-0 text-muted-foreground transition-transform group-data-[panel-open]/disclosure:rotate-90"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="grid gap-4 pt-1 pb-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** "MAIS INFORMAÇÕES" — a lista de disclosures colapsados, com divisor entre as
 *  linhas. Lista vazia some inteira (com o divisor da seção junto). */
export function VoucherMoreInfo({
  items,
}: Readonly<{ items: { key: string; title: string; blocks: VoucherDocumentBlock[] }[] }>) {
  const copy = useVoucherCopy();
  if (items.length === 0) return null;
  return (
    <>
      <Separator />
      <section className="grid">
        <VoucherEyebrow>{copy.moreInfo}</VoucherEyebrow>
        {items.map((item, i) => (
          <Fragment key={item.key}>
            {i > 0 ? <Separator /> : null}
            <VoucherDisclosure title={item.title}>
              {item.blocks.map((block) => (
                <VoucherFields key={block.key} fields={block.fields} />
              ))}
            </VoucherDisclosure>
          </Fragment>
        ))}
      </section>
    </>
  );
}

/** O retrato mínimo da linha de viajante: o `PersonAvatar` do kit, no tamanho da pílula. */
export function VoucherAvatar({
  name,
  photoUrl,
}: Readonly<{ name: string; photoUrl: string | null }>) {
  return <PersonAvatar name={name} photoUrl={photoUrl} size="xs" />;
}

/** A saia — a CONVERSA em botão de largura cheia + as MUDANÇAS (mover, cancelar)
 *  como botões visíveis logo abaixo. Nenhum botão sólido de propósito: o voucher é
 *  documento de LEITURA, não tem ação primária, e dentro da conversa um primary
 *  competiria com o composer.
 *
 *  Compartilhar NÃO fica aqui — subiu pro topo (`VoucherTopActions`), onde mora o
 *  share de mercado; na saia competia com a conversa (decisão do designer,
 *  2026-08-05). O kebab dessa mesma decisão — mover e cancelar atrás de UMA porta —
 *  caiu em 2026-08-31 (#2350): usuário real abriu o voucher para cancelar e não
 *  achou a opção. Mudar a reserva é o que se vem fazer no documento, não opção
 *  rara; cancelar sai em destrutivo SUTIL (a variante `destructive` do DS, tinta
 *  sem preenchimento sólido) e o par divide uma linha secundária. Sem handler o
 *  botão não nasce — é também como o host esconde cancelar num voucher morto
 *  (`isDeadVoucherStatus`) — e sem nenhuma das três ações a saia inteira some. */
export function VoucherActions({
  onTalkAbout,
  onMoveToTrip,
  onRequestCancel,
}: Readonly<{
  onTalkAbout?: () => void;
  onMoveToTrip?: () => void;
  onRequestCancel?: () => void;
}>) {
  const copy = useVoucherCopy();
  const withChanges = Boolean(onMoveToTrip ?? onRequestCancel);
  if (!onTalkAbout && !withChanges) return null;
  return (
    <div className="grid shrink-0 gap-2 px-6 pt-2 pb-6">
      {onTalkAbout ? (
        <WithTooltip text={copy.talkTooltip}>
          <Button variant="outline" className="w-full min-w-0" onClick={onTalkAbout}>
            <span className="truncate">{copy.talk}</span>
          </Button>
        </WithTooltip>
      ) : null}
      {withChanges ? (
        <div className="flex items-center gap-2">
          {onMoveToTrip ? (
            <Button variant="outline" className="min-w-0 flex-1" onClick={onMoveToTrip}>
              <ArrowsLeftRightIcon size={16} aria-hidden />
              <span className="truncate">{copy.moveToTrip}</span>
            </Button>
          ) : null}
          {onRequestCancel ? (
            <WithTooltip text={copy.cancelTooltip}>
              <Button variant="destructive" className="min-w-0 flex-1" onClick={onRequestCancel}>
                <ProhibitIcon size={16} aria-hidden />
                <span className="truncate">{copy.cancel}</span>
              </Button>
            </WithTooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
